/**
 * The legend for a map's dot colors.
 *
 * Mandatory, not decorative. The knock palette uses six hues including a
 * red/amber/green run, which is the worst case for colorblind readers; fill
 * and shape carry the distinction too (see `mapColors.ts`), and a legend is
 * what makes either readable. A map that colors dots without saying what the
 * colors mean is a puzzle, not a visualization.
 *
 * One component for both schemes so the swatch a user sees is rendered from
 * the same entry the pin is — the divergence `mapColors.ts` exists to end.
 */
import React from 'react';
import { dotHtml, type LegendEntry } from '@/lib/mapColors';

type Props = {
  entries: LegendEntry[];
  /** Optional per-key counts, shown after the label when supplied. */
  counts?: Record<string, number>;
  className?: string;
};

export default function MapLegend({ entries, counts, className = '' }: Props) {
  return (
    <ul
      className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-fg-secondary ${className}`}
    >
      {entries.map((e) => (
        <li key={e.key} className="flex min-w-0 items-center gap-1.5" title={e.detail}>
          <span
            aria-hidden="true"
            className="inline-flex shrink-0"
            // The swatch IS the marker markup, at a smaller size — so a hollow
            // ring reads hollow in the legend exactly as it does on the map.
            dangerouslySetInnerHTML={{ __html: dotHtml(e, 10) }}
          />
          <span className="truncate">
            {e.label}
            {counts && counts[e.key] !== undefined && (
              <span className="ml-1 text-fg-muted">
                {counts[e.key].toLocaleString()}
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}
